// TODO: Refatorar

/**
 * Fuso oficial dos atos do processo. Toda data e hora exibida ao usuário é lida aqui,
 * nunca no fuso do navegador: em processo arbitral data é prazo, e o mesmo instante
 * renderizado em dois fusos diferentes vira duas datas na tela — divergência que o
 * certificado, fonte da verdade da comprovação jurídica, não pode exibir.
 *
 * O Brasil aboliu o horário de verão em 2019, então `America/Sao_Paulo` é UTC-3 fixo.
 */
export const FUSO_BRASILIA = 'America/Sao_Paulo';

function toDate(date: string | Date | Record<string, never>): Date | null {
  if (typeof date === 'string') {
    return new Date(date);
  }
  if (date instanceof Date) {
    return date;
  }
  if (typeof date === 'object' && Object.keys(date).length === 0) {
    return null;
  }
  return null;
}

// TODO: remover — alias de `formatDate`; migrar os call-sites das features.
export function formatDateEuropean(
  date: string | Date | Record<string, never> | undefined | null,
): string {
  return formatDate(date);
}

// TODO: remover — `formatDateTimeAt` cobre o mesmo caso; migrar os call-sites das features.
export function formatDateTimeEuropean(
  date: string | Date | Record<string, never> | undefined | null,
): string {
  const dia = formatDate(date);
  if (!dia) return '';

  return `${dia} ${formatTime(date)}`;
}

export function parseEuropeanDate(dateString: string): Date | null {
  if (!dateString) return null;

  const parts = dateString.split('/');
  if (parts.length !== 3) return null;

  const day = Number.parseInt(parts[0], 10);
  const month = Number.parseInt(parts[1], 10) - 1;
  const year = Number.parseInt(parts[2], 10);

  const date = new Date(year, month, day);

  if (date.getDate() !== day || date.getMonth() !== month || date.getFullYear() !== year) {
    return null;
  }

  return date;
}

export function formatTime(date: string | Date | Record<string, never> | undefined | null): string {
  if (!date) return '';
  const targetDate = toDate(date);
  if (!targetDate || Number.isNaN(targetDate.getTime())) return '';

  return targetDate.toLocaleTimeString('pt-BR', {
    timeZone: FUSO_BRASILIA,
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatDate(date: string | Date | Record<string, never> | undefined | null): string {
  if (!date) return '';
  const targetDate = toDate(date);
  if (!targetDate || Number.isNaN(targetDate.getTime())) return '';

  return targetDate.toLocaleDateString('pt-BR', {
    timeZone: FUSO_BRASILIA,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

const DIA_EM_SAO_PAULO = new Intl.DateTimeFormat('en-CA', {
  timeZone: FUSO_BRASILIA,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const DIA_LONGO_NEUTRO = new Intl.DateTimeFormat('pt-BR', {
  timeZone: 'UTC',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

const OFFSET_SAO_PAULO = '-03:00';

const CHAVE_DE_DATA = /^\d{4}-\d{2}-\d{2}$/;
const INSTANTE_ISO = /^(\d{4}-\d{2}-\d{2})T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})$/;
const MEIA_NOITE_UTC = /T00:00:00(\.0+)?Z$/;

function diaEmSaoPaulo(instante: Date): string {
  const partes = DIA_EM_SAO_PAULO.formatToParts(instante);
  const parte = (tipo: Intl.DateTimeFormatPartTypes): string =>
    partes.find((p) => p.type === tipo)?.value ?? '';
  return `${parte('year')}-${parte('month')}-${parte('day')}`;
}

function dataQueExiste(chave: string): boolean {
  const [ano, mes, dia] = chave.split('-').map(Number);
  const d = new Date(Date.UTC(ano, mes - 1, dia));
  return d.getUTCFullYear() === ano && d.getUTCMonth() === mes - 1 && d.getUTCDate() === dia;
}

/**
 * Dia civil de Brasília (`yyyy-mm-dd`) em que um **instante de evento** aconteceu.
 *
 * É a chave de agrupamento correta para "o que já aconteceu no caso": `iso.slice(0, 10)`
 * devolve o dia **UTC**, então um ato praticado às 22h de 20/08 (BRT) cairia no grupo de
 * 21/08 e apareceria numa data em que nada aconteceu.
 *
 * Difere de `chaveDeDataDeCalendario`, que trata meia-noite UTC como coluna DATE e
 * devolve o dia UTC — correto para prazo, errado aqui: um evento gravado exatamente às
 * 21:00 BRT serializa como `T00:00:00.000Z` e voltaria como o dia seguinte.
 */
export function chaveDoDiaEmSaoPaulo(
  date: string | Date | Record<string, never> | undefined | null,
): string | null {
  if (!date) return null;
  const instante = toDate(date);
  if (!instante || Number.isNaN(instante.getTime())) return null;
  const chave = diaEmSaoPaulo(instante);
  return CHAVE_DE_DATA.test(chave) && dataQueExiste(chave) ? chave : null;
}

/**
 * Formata uma chave `yyyy-mm-dd` em `21 de agosto de 2026`.
 *
 * Não use `formatDateLong` para esse formato: `new Date('2026-08-21')` é meia-noite UTC,
 * que em Brasília é 21h de 20/08 — o cabeçalho sairia um dia antes dos itens que agrupa.
 * O `timeZone: 'UTC'` aqui não contraria `FUSO_BRASILIA`: a chave recebida **já é** o dia
 * civil de Brasília, e ler em UTC é o que a preserva intacta.
 */
export function formatDateKeyLong(dateKey: string | null | undefined): string {
  if (!dateKey || !CHAVE_DE_DATA.test(dateKey) || !dataQueExiste(dateKey)) return '';
  const [ano, mes, dia] = dateKey.split('-').map(Number);
  return DIA_LONGO_NEUTRO.format(new Date(Date.UTC(ano, mes - 1, dia)));
}

/**
 * Normaliza um valor de data de calendário para a chave `yyyy-mm-dd`, ou `null`.
 *
 * Trata as três formas que o core-api emite para o mesmo conceito:
 * - chave nua (`'2026-09-01'`) — devolvida como está, se o dia existir no calendário;
 * - instante em **meia-noite UTC** (`'2026-09-01T00:00:00.000Z'`) — é coluna DATE
 *   serializada por `.toISOString()`, então a data é a própria parte de data;
 * - instante **com hora do dia** — é evento, e o dia que vale é o de São Paulo.
 *
 * Recusa data inexistente (`'2026-02-31'`) e string fora do ISO, em vez de deixar o
 * parser permissivo do `Date` rolar para o mês seguinte.
 */
export function chaveDeDataDeCalendario(valor: unknown): string | null {
  if (typeof valor !== 'string' || valor === '') return null;
  if (CHAVE_DE_DATA.test(valor)) return dataQueExiste(valor) ? valor : null;
  const partes = INSTANTE_ISO.exec(valor);
  if (!partes || !dataQueExiste(partes[1])) return null;
  const instante = new Date(valor);
  if (Number.isNaN(instante.getTime())) return null;
  if (MEIA_NOITE_UTC.test(valor)) return partes[1];
  const chaveLocal = diaEmSaoPaulo(instante);
  if (!CHAVE_DE_DATA.test(chaveLocal) || !dataQueExiste(chaveLocal)) return null;
  return chaveLocal;
}

/**
 * Instante ISO em que um dia de calendário **começa em São Paulo** (00:00 BRT).
 *
 * Inverso de `chaveDeDataDeCalendario`, para quando o backend exige instante e não a
 * chave nua. `new Date('2026-08-10T00:00:00')` não serve: a string sem offset é lida no
 * fuso de quem executa, então em UTC ela vira `2026-08-10T00:00:00Z` — 21:00 de 09/08 em
 * Brasília, ou seja, o dia informado é gravado como o anterior. O offset entra explícito
 * (o Brasil aboliu o horário de verão em 2019, então `America/Sao_Paulo` é UTC-3 fixo),
 * o que torna o resultado independente do fuso do navegador e do runner de teste.
 *
 * Recusa o que não é dia de calendário — string fora de `yyyy-mm-dd` e data inexistente
 * (`2026-02-31`) — em vez de deixar o parser permissivo do `Date` rolar para o mês seguinte.
 */
export function instanteDoDiaEmSaoPaulo(dia: string): string {
  if (!ehDiaDeCalendario(dia)) {
    throw new Error(`Dia de calendário inválido: ${dia}`);
  }
  return new Date(`${dia}T00:00:00.000${OFFSET_SAO_PAULO}`).toISOString();
}

const HORA_DO_DIA = /^([01]\d|2[0-3]):[0-5]\d$/;

/** `HH:mm` de um relógio de 24 h — a forma que `instanteEmSaoPaulo` aceita. */
export function ehHoraDoDia(hora: string): boolean {
  return HORA_DO_DIA.test(hora);
}

/**
 * `yyyy-mm-dd` de um dia que existe no calendário — a forma que `instanteDoDiaEmSaoPaulo` e
 * `instanteEmSaoPaulo` aceitam. Para validar antes de chamá-las, que lançam.
 */
export function ehDiaDeCalendario(dia: string): boolean {
  return CHAVE_DE_DATA.test(dia) && dataQueExiste(dia);
}

/**
 * Instante ISO de um dia + hora (`HH:mm`) informados no relógio de São Paulo — o par de
 * `instanteDoDiaEmSaoPaulo` para quando a hora é conhecida (ex.: o horário de um ato que a
 * Secretaria registra). Mesmo offset fixo e mesma recusa de dia inexistente.
 *
 * @example instanteEmSaoPaulo('2026-09-17', '16:30') // '2026-09-17T19:30:00.000Z'
 */
export function instanteEmSaoPaulo(dia: string, hora: string): string {
  if (!ehDiaDeCalendario(dia) || !ehHoraDoDia(hora)) {
    throw new Error(`Dia ou hora inválidos: ${dia} ${hora}`);
  }
  return new Date(`${dia}T${hora}:00.000${OFFSET_SAO_PAULO}`).toISOString();
}

/**
 * Formata uma data de calendário (`yyyy-mm-dd` ou instante em meia-noite UTC) em `dd/mm/aaaa`.
 *
 * Não use `formatDate` para esse formato: ele renderiza no fuso de Brasília, onde a meia-noite
 * UTC de uma data de calendário ainda é a véspera — o vencimento sairia um dia antes. Para
 * **instante** com hora (vencimento de prazo arbitral, evento), `formatDate` é o certo.
 */
export function formatDateKey(dateKey: string | null | undefined): string {
  if (!dateKey) return '';
  if (!CHAVE_DE_DATA.test(dateKey)) return formatDate(dateKey);
  const chave = chaveDeDataDeCalendario(dateKey);
  if (chave === null) return formatDate(dateKey);
  const [ano, mes, dia] = chave.split('-');
  return `${dia}/${mes}/${ano}`;
}

/**
 * Formata um prazo (data de calendário) em `dd/mm/aaaa`, aceitando as DUAS formas
 * em que o core-api serializa um vencimento: a chave nua (`'2026-09-01'`) e o
 * instante ISO em meia-noite UTC (`'2026-09-01T00:00:00.000Z'`, o que
 * `businessDayService` de fato emite via `.toISOString()`).
 *
 * Difere de `formatDateKey`, que por contrato só trata a chave nua e delega
 * qualquer valor com hora ao `formatDate` — correto para instante de evento,
 * errado para prazo: em fuso negativo o vencimento apareceria um dia antes.
 */
export function formatDataPrazo(prazo: string | null | undefined): string {
  const chave = chaveDeDataDeCalendario(prazo);
  if (chave === null) return '';
  const [ano, mes, dia] = chave.split('-');
  return `${dia}/${mes}/${ano}`;
}

/**
 * Chave `yyyy-mm-dd` do dia em tempo universal, ou `null`.
 *
 * É a redução que a régua de prazo do repo usa (`dateUtils.diasAte`), e existe para que
 * contagem e rótulo do mesmo vencimento partam de **uma** redução: reduzir por
 * `chaveDeDataDeCalendario` de um lado e por dia universal do outro produz frases que se
 * contradizem ("Vence hoje · até 31 de agosto") quando o instante cai entre 0h e 3h UTC.
 *
 * Difere de `chaveDoDiaEmSaoPaulo`, que é a redução de **evento**.
 */
export function chaveDoDiaEmUtc(valor: string | null | undefined): string | null {
  if (!valor) return null;
  if (CHAVE_DE_DATA.test(valor)) return dataQueExiste(valor) ? valor : null;
  if (!INSTANTE_ISO.test(valor)) return null;
  const instante = new Date(valor);
  if (Number.isNaN(instante.getTime())) return null;
  const mes = `${instante.getUTCMonth() + 1}`.padStart(2, '0');
  const dia = `${instante.getUTCDate()}`.padStart(2, '0');
  return `${instante.getUTCFullYear()}-${mes}-${dia}`;
}

/**
 * Renderiza a chave de um prazo com o formatador dado.
 *
 * Os formatadores de prazo são todos pinados em UTC e recebem a chave já reduzida por
 * `chaveDeDataDeCalendario`: é isso que os mantém fora do relógio do navegador. Ler o
 * prazo como instante o mostraria um dia antes, porque o core-api normaliza vencimento
 * para meia-noite UTC (`diasCivis.ts:inicioDoDiaUtc`).
 */
function formatarChaveDoPrazo(
  formatador: Intl.DateTimeFormat,
  prazo: string | null | undefined,
): string {
  const chave = chaveDeDataDeCalendario(prazo);
  if (chave === null) return '';
  const [ano, mes, dia] = chave.split('-').map(Number);
  return formatador.format(new Date(Date.UTC(ano, mes - 1, dia)));
}

/** `1 de setembro de 2026`. */
export function formatDataPrazoLongo(prazo: string | null | undefined): string {
  return formatarChaveDoPrazo(DIA_LONGO_NEUTRO, prazo);
}

const DIA_E_MES_NEUTRO = new Intl.DateTimeFormat('pt-BR', {
  timeZone: 'UTC',
  day: 'numeric',
  month: 'long',
});

/** `1 de setembro` — para chip curto, onde o ano não cabe. */
export function formatDataPrazoSemAno(prazo: string | null | undefined): string {
  return formatarChaveDoPrazo(DIA_E_MES_NEUTRO, prazo);
}

const DIA_ABREVIADO_NEUTRO = new Intl.DateTimeFormat('pt-BR', {
  timeZone: 'UTC',
  day: '2-digit',
  month: 'short',
  year: 'numeric',
});

/** `01 de set. de 2026` — pareia com `formatDateShortWithMonthName`, para prazo. */
export function formatDataPrazoAbreviada(prazo: string | null | undefined): string {
  return formatarChaveDoPrazo(DIA_ABREVIADO_NEUTRO, prazo);
}

export function formatDateTimeFull(
  date: string | Date | Record<string, never> | undefined | null,
): string {
  if (!date) return '';
  const targetDate = toDate(date);
  if (!targetDate || Number.isNaN(targetDate.getTime())) return '';

  return targetDate.toLocaleDateString('pt-BR', {
    timeZone: FUSO_BRASILIA,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * `DD/MM/AAAA às HH:MM` — data e hora em texto corrido, como no design.
 * Difere de `formatDateTimeFull`, que separa por vírgula (`DD/MM/AAAA, HH:MM`).
 */
export function formatDateTimeAt(
  date: string | Date | Record<string, never> | undefined | null,
): string {
  const dia = formatDate(date);
  if (!dia) return '';

  return `${dia} às ${formatTime(date)}`;
}

export function formatDateLong(
  date: string | Date | Record<string, never> | undefined | null,
): string {
  if (!date) return '';
  const targetDate = toDate(date);
  if (!targetDate || Number.isNaN(targetDate.getTime())) return '';

  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: FUSO_BRASILIA,
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(targetDate);
}

export function formatDateLongWithWeekday(
  date: string | Date | Record<string, never> | undefined | null,
): string {
  if (!date) return '';
  const targetDate = toDate(date);
  if (!targetDate || Number.isNaN(targetDate.getTime())) return '';

  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: FUSO_BRASILIA,
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(targetDate);
}

export function formatDateShortWithMonthName(
  date: string | Date | Record<string, never> | undefined | null,
): string {
  if (!date) return '';
  const targetDate = toDate(date);
  if (!targetDate || Number.isNaN(targetDate.getTime())) return '';

  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: FUSO_BRASILIA,
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(targetDate);
}

export function formatDateTimeShort(
  date: string | Date | Record<string, never> | undefined | null,
): string {
  if (!date) return '';
  const targetDate = toDate(date);
  if (!targetDate || Number.isNaN(targetDate.getTime())) return '';

  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: FUSO_BRASILIA,
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(targetDate);
}

export function formatRelativeTimelineDate(
  date: string | Date | Record<string, never> | undefined | null,
): string {
  if (!date) return '';
  const targetDate = toDate(date);
  if (!targetDate || Number.isNaN(targetDate.getTime())) return '';

  const now = new Date();
  const diffMs = now.getTime() - targetDate.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 60) {
    return `Há ${diffMins} ${diffMins === 1 ? 'minuto' : 'minutos'}`;
  }

  if (diffHours < 24) {
    return `Há ${diffHours} ${diffHours === 1 ? 'hora' : 'horas'}`;
  }

  if (diffDays < 7) {
    return `Há ${diffDays} ${diffDays === 1 ? 'dia' : 'dias'}`;
  }

  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: FUSO_BRASILIA,
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(targetDate);
}

/**
 * Chave `yyyy-mm-dd` de `days` dias a partir de hoje, contada em Brasília.
 *
 * `new Date().toISOString()` daria o dia UTC: depois das 21h BRT o teto de agendamento
 * saltaria um dia à frente do que o usuário vê no calendário.
 */
export function getMaxScheduleDate(days: number): string {
  const [ano, mes, dia] = diaEmSaoPaulo(new Date()).split('-').map(Number);
  return new Date(Date.UTC(ano, mes - 1, dia + days)).toISOString().slice(0, 10);
}
